/**
 * Wire types for the descriptors feature (PX-W6.5) — `GET /descriptors/agents`
 * serves each row's stored payload VERBATIM, and the seeder's payload shape
 * is `{"id","name","category","description"}` (DescriptorsSeeder.seedAgents —
 * pinned byte-for-byte by `descriptorsContract.test.ts`). There is no `slug`
 * on the served row; the Mongo row's slug column never reaches the wire.
 *
 * The key field is therefore resolved defensively: `id` first, `slug` as the
 * fallback, so a future payload that switches either way keeps rendering
 * (fix-back D1: the original guard required `slug` and dropped EVERY served
 * row, rendering the empty state against a live list).
 *
 * `agent-tools-via-mcp` (px memory): agent definitions arrive through the
 * hosted px MCP surface — this feature is the READ view only; there is no
 * editing, no inline tool calls, no agent-runtime verb here.
 */

/** The normalised catalogue row the page renders. */
export interface AgentDescriptor {
  /** The stable wire key — `id` on every served row today, `slug` fallback. */
  key: string
  name: string
  description?: string
  category?: string
}

/**
 * Narrow one unknown descriptor row to {@link AgentDescriptor}, resolving
 * the key from `id` or `slug`. Returns null for a row carrying neither key
 * field (or no non-empty `name`) — dropped, never rendered half-empty
 * (plan Decision 9).
 */
export function asAgentDescriptor(value: unknown): AgentDescriptor | null {
  if (typeof value !== 'object' || value === null) return null
  const row = value as Record<string, unknown>
  const id = typeof row['id'] === 'string' ? row['id'] : ''
  const slug = typeof row['slug'] === 'string' ? row['slug'] : ''
  const key = id !== '' ? id : slug
  if (key === '') return null
  if (typeof row['name'] !== 'string' || row['name'] === '') return null
  return {
    key,
    name: row['name'],
    description: typeof row['description'] === 'string' ? row['description'] : undefined,
    category: typeof row['category'] === 'string' ? row['category'] : undefined
  }
}

/** A task-status descriptor row (rendered where a status vocabulary is shown). */
export interface TaskStatusDescriptor {
  key: string
  label: string
  [field: string]: unknown
}
